import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import dynamic from 'next/dynamic';

import ShortForm from 'patterns/ShortForm/ShortForm';
import ProductPageOpenFormSection from 'patterns/ProductPageOpenFormSection/ProductPageOpenFormSection';
import RegistrationUnavailableLanguage from 'patterns/RegistrationUnavailableLanguage/RegistrationUnavailableLanguage';
import useOpenFormVisibility from 'utils/hooks/useOpenFormVisibility/useOpenFormVisibility';
import { DEFAULT_LOCALE } from 'utils/i18nUtils';
import { CREDIT_CARD } from 'utils/prospectTypes';
import Experiment from '@nubank/www-latam-commons/components/Experiment/Experiment';
import { useExperimentInfo } from '@nubank/www-latam-commons/components/Experiment/ExperimentsContext';
import { SHORT_TIME_EXPERIMENT, PROGRESSIVE_DISCLOSURE_FORM_V2 } from 'utils/experiments';

const ProgressiveDisclosureForm = dynamic(import('patterns/ProgressiveDisclosureForm/ProgressiveDisclosureForm'), { ssr: false });

function SectionForm({ onFormVisibilityChange }) {
  const { locale } = useIntl();
  const [ref, isFormHidden] = useOpenFormVisibility();

  const { variant } = useExperimentInfo(SHORT_TIME_EXPERIMENT.id);

  useEffect(() => {
    onFormVisibilityChange(isFormHidden);
  }, [isFormHidden]);

  return (
    <ProductPageOpenFormSection ref={ref}>
      <ProductPageOpenFormSection.Container
        gridTemplateRows={{ xs: 'auto 14rem 1rem 1fr', md: 'auto 1fr', lg: '1fr' }}
        gridRowGap={{ xs: '2x', md: '6x' }}
        overflow="hidden"
      >
        <ProductPageOpenFormSection.Heading
          gridColumn={{ xs: '1 / span 4', md: '1 / span 5', lg: '1 / span 4' }}
        >
          <Typography
            variant="subtitle"
            tag="h1"
            color="black"
            textAlign={{ xs: 'center', md: 'left' }}
            intlKey="cms.cardPage.cardHeroTitle"
            marginBottom={{ xs: '2x', md: '6x' }}
          />
          <Typography
            variant="title"
            tag="h2"
            color="black"
            textAlign={{ xs: 'center', md: 'left' }}
            intlKey="cms.cardPage.cardHeroCtaDescription"
          />
        </ProductPageOpenFormSection.Heading>
        <ProductPageOpenFormSection.ImageBlock
          gridColumn={{ xs: '1 / span 4', md: '1 / span 5', lg: '5 / span 4' }}
          gridRow={{ xs: '2 / span 2', md: '2', lg: '1' }}
          margin="0x"
          height={{ xs: 'calc(100% + 21rem)', lg: 'auto' }}
          srcSet={{
            xs: {
              '1x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-xs@1x.png',
              '2x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-xs@2x.png',
              '3x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-xs@3x.png',
            },
            md: {
              '1x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-md@1x.png',
              '2x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-md@2x.png',
              '3x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-md@3x.png',
            },
            lg: {
              '1x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-lg@1x.png',
              '2x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-lg@2x.png',
              '3x': 'roxinho-basicos-flutuantes/roxinho-basicos-flutuantes-lg@3x.png',
            },
          }}
        />
        <ProductPageOpenFormSection.FormBlock
          gridColumn={{ xs: '1 / span 4', md: '4 / span 3', lg: '9 / span 4' }}
          gridRow={{ xs: '3 / span 2', md: '2', lg: '1' }}
        >
          {locale === DEFAULT_LOCALE ? (
            <Experiment id={PROGRESSIVE_DISCLOSURE_FORM_V2.id}>
              <Experiment.Original>
                <ShortForm
                  prospectTypeKey={CREDIT_CARD}
                  formTitle={variant === SHORT_TIME_EXPERIMENT.shortTimeAnswer
                    ? 'SHORT_TIME_EXPERIMENT.FORM_TITLE'
                    : 'SHORT_FORM.CREDIT_CARD.FORM_TITLE'}
                  completeFormTitle="SHORT_FORM.CREDIT_CARD.COMPLETE_FORM_TITLE"
                  completeFormVerticalTitle="SHORT_FORM.CREDIT_CARD.COMPLETE_FORM_VERTICAL_TITLE"
                />
              </Experiment.Original>
              <Experiment.Variant id={PROGRESSIVE_DISCLOSURE_FORM_V2.emailsFieldsForm}>
                <ProgressiveDisclosureForm
                  prospectTypeKey={CREDIT_CARD}
                  formTitle={variant === SHORT_TIME_EXPERIMENT.shortTimeAnswer
                    ? 'SHORT_TIME_EXPERIMENT.FORM_TITLE'
                    : 'SHORT_FORM.CREDIT_CARD.FORM_TITLE'}
                  completeFormName="completeFormB"
                />
              </Experiment.Variant>
              <Experiment.Variant
                id={PROGRESSIVE_DISCLOSURE_FORM_V2.emailFieldConfirmationStepForm}
              >
                <ProgressiveDisclosureForm
                  prospectTypeKey={CREDIT_CARD}
                  formTitle={variant === SHORT_TIME_EXPERIMENT.shortTimeAnswer
                    ? 'SHORT_TIME_EXPERIMENT.FORM_TITLE'
                    : 'SHORT_FORM.CREDIT_CARD.FORM_TITLE'}
                  completeFormName="completeFormC"
                />
              </Experiment.Variant>
            </Experiment>
          ) : (
            <RegistrationUnavailableLanguage isOpenForm isShortForm />
          )}
        </ProductPageOpenFormSection.FormBlock>
      </ProductPageOpenFormSection.Container>
    </ProductPageOpenFormSection>
  );
}

SectionForm.propTypes = {
  onFormVisibilityChange: PropTypes.func.isRequired,
};

export default React.memo(SectionForm);
